// HeaderSearch — the global "Search across everything…" pill.
//
// Extracted from MainNavbar (2026-06-08 per Nika "unwrap AppHeader to one
// flex navbar"): both the flat AppHeader and the icon-row MainNavbar (the
// canvas + analytics headers) now reference this ONE definition by key
// instead of duplicating the search / apps-dropdown wiring. Each consumer
// sets its OWN layout — MainNavbar stretches it (`flex: 1`), AppHeader pins
// it to a fixed centered pill (`width: H3`) — while the shared chrome and
// behaviour live here.
//
// Wiring: `el.call('globalSearch', value)` populates `root.navSearchResults`
// + `root._navSearchGrouped`; the Dropdown.ResultGroups slot renders them
// via `NavSearchResultGroup`. The apps launcher (`openSearchAppsDropdown`)
// opens on an empty-input focus and is registered by the WORKSPACE
// functions package — guarded so shells without it keep a plain input.
export const HeaderSearch = {
  // FA204 / frank trap — openSearchSurface/chevronOpen used to be bare
  // module-scope functions read from inside `onMousedown`/`onClick`/`onFocus`
  // factories; they read as undefined once this component is frank.toJSON'd
  // for platform delivery (workspace d6ea1142,
  // project_frank_drops_module_local_helpers). Moved into `scope`;
  // chevronOpen calls its sibling via `this.openSearchSurface` (object-
  // literal properties can't see each other as bare identifiers — only
  // `el.scope.<name>()` binds `this` to the scope bag).
  scope: {
    // ONE open policy for every trigger (chevron click + input focus): text
    // in the field re-runs the query (globalSearch owns _navSearchOpen), and
    // the workspace apps launcher opens either way — with text it lists the
    // results itself (9A, the one search dropdown). Open-only and
    // idempotent — ensureSearchLauncher opens only when the launcher is not
    // already the open layer — so the two triggers can never toggle each
    // other net-closed. The functions are registered by the WORKSPACE
    // package; shells without them keep a plain input.
    // `el` is always the search INPUT (every caller passes it: the input's own
    // onFocus, chevronOpen's resolved `input`, the Cmd+K handler). `anchor` is
    // the PILL — the panel is positioned and width-measured off it. The two are
    // passed separately on purpose: `restoreEl` is the a11y focus-restore
    // target the dropdown plugin re-focuses on close, and that must be the
    // input the user is typing in, never the pill (A2-08 — see
    // workspace/functions/searchApps.js openSearchAppsDropdown).
    openSearchSurface(el, anchor) {
      const q = (el?.node?.value || '').trim()
      try {
        // THE ONE SEARCH DROPDOWN (9A — DESIGN-WL-PAGESLOT-OPEN-SPLIT-SEARCH-1):
        // the launcher opens for text too — with text it shows the results
        // itself (workspace SearchAppsMenu). The query re-runs as before.
        if (q) el.call('globalSearch', q)
        el.call('ensureSearchLauncher', { anchorEl: anchor || el, restoreEl: el })
      } catch (err) {
        console.warn('[HeaderSearch] search open unavailable:', err)
      }
    },
    // Chevron trigger body — focus the input (a no-op when already focused,
    // so no spurious focus event) and run the shared open policy.
    chevronOpen(e, el) {
      e.preventDefault()
      e.stopPropagation()
      const input = el.parent?.Form?.Input
      if (input?.node?.focus) input.node.focus()
      this.openSearchSurface(input || el, el.parent || el)
    },
    // The keys the ⌘K hint names on THIS device. The binding
    // (onDocumentKeydown) takes either modifier on every platform; the
    // hint names the one printed on the keyboard in front of the user.
    shortcutKeys() {
      const nav = typeof navigator === 'undefined' ? null : navigator
      const platform = String(
        (nav && ((nav.userAgentData && nav.userAgentData.platform) || nav.platform || nav.userAgent)) || ''
      )
      return [/mac|iphone|ipad|ipod/i.test(platform) ? '⌘' : 'Ctrl', 'K']
    }
  },
  extends: 'SearchBar',
  round: 'radiusPill',
  position: 'relative',
  // ON THE ROW CENTRE LINE. SearchBar carries an optical `margin:
  // '-V - - -'` — a NEGATIVE TOP margin (0.0902em, 1.63px at this pill's
  // font size) — and this pill is absolutely centred by its consumer
  // (`top: 50%` + translate), so that nudge lifted it off the centre line:
  // measured live at 1920px, the search read y2 while the leading bar, the
  // tab strip and the trailing bar all read y5. A real value, never null —
  // a null CSS prop deletes the inherited declaration instead of setting
  // one. MainNavbar already passes its own `margin: '0 A'` at the mount, so
  // the icon-row variant is unaffected.
  margin: '0',
  // The PILL is not a tab stop — its input is (A2-08 companion). SearchBar
  // extends the legacy `DropdownParentFocus`, whose `tabindex: '0'` exists so
  // a CSS-only `:focus-within` panel can be reached by keyboard. HeaderSearch
  // never ships that reveal (its results panel is state-gated and its launcher
  // is a @symbo.ls/dropdown layer), so the attribute bought nothing and cost
  // two real defects:
  //   • a DUPLICATE tab stop — Tab landed on the `<search>` wrapper, then on
  //     the input inside it, for one control;
  //   • a focus TRAP — closeDropdown re-focuses the closing layer's
  //     `restoreEl`, and a focusable wrapper accepted that focus. Tabbing out
  //     of the field fired the launcher's own blur-close 200ms later, which
  //     threw focus back onto the pill: measured live, Tab could never leave
  //     the search field (Input → ClearSearchMorph → HomeBtn → back to the
  //     pill, forever).
  // Without the attribute the wrapper is unfocusable, so the plugin's restore
  // is a no-op on it and the trap cannot form. `:focus-within` is unaffected —
  // it matches on a DESCENDANT holding focus and never needed the tabindex.
  tabindex: null,

  // SEARCH-1 (2026-08-14, per Nika review — Slack/Linear/Notion/GitHub all
  // bind Cmd+K): global keyboard shortcut so search opens from anywhere, not
  // just by clicking the pill. Bound once per mounted instance (guarded —
  // AppHeader + MainNavbar can each carry their own HeaderSearch, and only
  // the visible one's input receives focus in practice) and torn down on
  // unmount so a hidden/removed navbar variant never leaves a dangling
  // document-level listener. Focusing the input re-uses the SAME open policy
  // every other trigger (chevron, click) already funnels through — this
  // never invents a second "how search opens" path.
  // FA503: declared, so the registration IS the "bound once per mounted
  // instance" guarantee the note above describes — DOMQL registers this
  // document listener once when the element takes its node and removes it in
  // dispose(). The `__hsKbBound` / `__hsKbUnbind` pair of fields, and the
  // `onDestroy` hook that existed only to run the un-binder, are gone.
  onDocumentKeydown: (ev, el, s) => {
    if (!(ev.metaKey || ev.ctrlKey) || ev.key?.toLowerCase() !== 'k') return
    ev.preventDefault()
    const input = el.Form?.Input
    if (!input?.node) return
    // THE SHORTCUT REVEALS THE PILL. In the flat AppHeader this pill is the
    // search MORPH — opacity 0 and no pointer events until `_headerSearchOpen`
    // — and ⌘K used to focus that invisible input and open the launcher
    // under it, so typed text went into a field nobody could see (live on
    // dev 0ffad3a40, 2026-09-26). This is the open SearchBtn performs, with
    // whatever held focus before the shortcut as the return target when the
    // search closes. Where no morph reads the flag (MainNavbar's always-
    // visible pill) it only says "search is open".
    const root = s && s.root
    if (root && !root._headerSearchOpen) {
      // Focus goes back where it was when the search closes — or, when
      // nothing held it (the page body), to the control that owns the pill
      // (the host names it: AppHeader's SearchBtn). Without a return target
      // Escape left focus on the hidden input and the launcher it keeps open
      // (live on dev 2c2b6d978).
      const prev = typeof document === 'undefined' ? null : document.activeElement
      const held = prev && prev !== document.body && !(el.node && el.node.contains(prev))
      const back = held ? prev : el.scope.searchReturnTarget ? el.scope.searchReturnTarget(el) : null
      if (back) root.__headerSearchReturnFocusTo = back
      root.update({ _headerSearchOpen: true })
    }
    input.node.focus()
    input.node.select?.()
    el.scope.openSearchSurface(input, el)
  },

  // Right-edge chevron — a REAL trigger for the same open the input focus
  // performs. It cannot stay decorative (pointerEvents:none pass-through):
  // an already-focused input re-fires no focus event, so after any dismissal
  // that keeps focus the pass-through click opened nothing. onMousedown
  // preventDefault keeps the press from blurring the input, so the 200ms
  // blur-close timer never races the open.
  ArrowDown: {
    extends: 'Icon',
    name: 'arrowAngleDown',
    position: 'absolute',
    right: 'A',
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'caption',
    boxSize: 'A',
    cursor: 'pointer',
    role: 'button',
    'aria-label': (el) => el.call('polyglot', 'shell.headerSearch.openDropdown'),
    // Mousedown-first like the HeaderTabs triggers (re-render node swaps eat
    // click events; preventDefault also keeps the press from blurring the
    // input, so the 200ms blur-close never races the open). onClick remains
    // as the keyboard/assistive path — both are idempotent.
    onMousedown: (e, el) => {
      if (e.button === 0) el.scope.chevronOpen(e, el)
    },
    onClick: (e, el) => el.scope.chevronOpen(e, el)
  },

  Form: {
    // SearchBar sizes this glyph with `fontSize: 'Z1'` and no box. Icon's box
    // comes from width/height, and the spacing scale is em-relative, so the
    // glyph tracked whatever font-size this slot inherited — measured 15.54px
    // against the header row's other controls. Overridden HERE rather than in
    // SearchBar: that component is generic and every other search bar renders
    // correctly on its own font-size.
    // The launcher's PIXEL search glyph (dropdowns spec 2026-09-30, Nika's pick): whole cells at iconSm.
    Icon: { name: 'pxSearch', boxSize: 'iconSm' },
    Input: {
      // THE CONTROL SCALE OF THIS VARIANT — one step down the spacing
      // sequence from SearchBar's `Y1`, horizontal insets unchanged (`B2`
      // clears the chevron, `C` clears the glyph).
      //
      // The pill is as tall as this input (line box + 2 × vertical padding)
      // and the spacing sequence is em-relative, so at the header's
      // `fontSize: '1.0625rem'` (18.0625px) `Y1` resolved to 8.528px a side
      // and the search rendered 41px against the 38px row every other
      // header bar shares. `Y` resolves to 6.900px, which lands the input at
      // 37.80px inside the pill's `HEADER_ISLAND_MIN_HEIGHT` box (38.42px) —
      // the islands' own gauge. The type size is deliberately NOT part of
      // the cure: it stays the shell's search/prompt context.
      //
      // Declared HERE, on the variant, rather than at AppHeader's mount:
      // Rule 68 bans a call-site padding override and prescribes exactly
      // this — a named variant with its own control scale. SearchBar keeps
      // `Y1` for every other search bar in the product.
      // See __tests__/headerSearchRowHeight.test.js.
      padding: 'Y B2 Y C',
      round: 'radiusPill',
      border: 'none',
      borderWidth: '0',
      borderStyle: 'none',
      theme: 'transparent',
      outline: 'none',
      appearance: 'none',
      boxShadow: 'none',
      placeholder: (el) => el.call('polyglot', 'shell.headerSearch.placeholder'),
      // The command names its shortcut to assistive technology (the
      // keycaps beside it stay visual): onDocumentKeydown takes either
      // modifier on every platform.
      ariaKeyshortcuts: 'Meta+K Control+K',
      value: (el, s) => s.root?.navSearchQuery || '',
      // Focus always opens SOMETHING (the old empty-only guard dead-ended on
      // stale text a dismissal left in the uncontrolled input): the launcher
      // opens, and text re-runs the search so its results come back (9A: the
      // launcher lists them). Anchor on the pill (two parents up: Input →
      // Form → HeaderSearch) so the panel aligns + sizes to the FULL search
      // width.
      onFocus: (e, el) => {
        // A close restores focus HERE (this input is the layer's a11y restore
        // target — A2-08), so a focus arriving right after a dismissal is the
        // close's own doing, not the user's. Re-opening on it made Escape,
        // outside-click and tile picks look inert. Deliberate focus (a click
        // into the field, Cmd+K, the chevron) arrives outside that window.
        try {
          if (el.call('searchAppsJustDismissed')) return
        } catch (_) {}
        el.scope.openSearchSurface(el, el.parent?.parent || el)
      },
      // Close on blur — debounced so a tile mousedown in the shell-root panel
      // (which blurs the input) still lands its click before the close; the
      // panel's own mouseenter cancels the timer while the cursor is inside.
      onBlur: (e, el) => {
        try {
          el.call('scheduleSearchAppsClose')
        } catch (err) {
          console.warn('[HeaderSearch] scheduleSearchAppsClose unavailable:', err)
        }
        // The results panel is state-gated (the :focus-within reveal never
        // ships — see the Dropdown block below), so blur must close it too,
        // with the same grace period so an in-panel click still lands first.
        const pill = el.parent?.parent
        setTimeout(() => {
          if (pill?.node && pill.node.contains(document.activeElement)) return
          // 9A — the launcher is the search's ONE dropdown (it hosts the
          // results), so focus INSIDE it (a tile, a result row) is focus
          // inside the search, exactly as scheduleSearchAppsClose (workspace
          // searchApps.js) counts it. A primary press on a tile moves focus
          // there; ending the search here closed the launcher 200 ms into a
          // slow press or a tile drag (DESIGN-WL-PAGESLOT-OPEN-SPLIT-SEARCH-1).
          const active = document.activeElement
          if (
            active &&
            typeof active.closest === 'function' &&
            active.closest('[data-smbls-dropdown="panel"]') &&
            typeof el?.context?.functions?.searchLauncherIsOpen === 'function' &&
            el.call('searchLauncherIsOpen')
          ) {
            return
          }
          // A result row's "Pin to navbar" context menu (NavSearchResultItem.
          // onContextmenu, workspace/components/NavSearchDropdown.js — opened
          // with `fromNavSearch: true`) blurs this input BY DESIGN: the house
          // ContextDropdown is a single global portal mounted OUTSIDE this
          // pill's subtree, so it can never satisfy the `contains` check
          // above the way an in-panel click does. Respect it the same way
          // scheduleSearchAppsClose (searchApps.js) respects a still-focused
          // input — skip the close while it's up — and resume the instant it
          // closes: Escape / outside click / the Pin action itself all null
          // out `contextDropdown` (see ContextDropdown.js), so there is
          // nothing here to separately track or clean up.
          const dd = el.state?.root?.contextDropdown
          if (dd && dd.open && dd.fromNavSearch) return
          if (typeof el?.context?.functions?.closeNavSearch === 'function') {
            el.call('closeNavSearch')
          }
        }, 200)
      },
      // A2-08 — until 9A the first keystroke closed the apps launcher. Pre
      // FW-DROPDOWN-CLOSE-RESTORES-FOCUS-UNCONDITIONALLY-1, @symbo.ls/dropdown's
      // closeDropdown re-focused the closing layer's `restoreEl`
      // UNCONDITIONALLY; that target is set at OPEN time (onFocus, above, and
      // the ensureSearchLauncher call here) and must be THIS input — when it
      // defaulted to the anchor pill, the close moved focus onto the pill's
      // own focusable `<search>` element and every keystroke after the first was
      // dropped, so the field kept only 'w' of 'warranty'. closeDropdown now
      // restores only when the close would otherwise lose focus, but this
      // input is still passed as `restoreEl` explicitly for the other close
      // paths (Escape, outside-click) that DO need to land here rather than
      // on the pill.
      onInput: (e, el) => {
        el.call('globalSearch', e.target.value)
        // The launcher STAYS OPEN while typing (9A): it is the one search
        // dropdown, and with text it lists the results in place of the tiles.
        // `ensureSearchLauncher` opens it only when it is not already the open
        // layer (a dismissal closed it) — never a re-mount per keystroke.
        try {
          el.call('ensureSearchLauncher', {
            anchorEl: el.parent?.parent || el,
            restoreEl: el
          })
        } catch (err) {
          console.warn('[HeaderSearch] search launcher (input) unavailable:', err)
        }
      },
      onKeydown: (e, el) => {
        // SEARCH-1 (2026-08-14): Down/Up move the keyboard selection through
        // the grouped results; Enter opens whatever's highlighted (or the
        // top hit if nothing's been arrow-key-selected yet). All three
        // preventDefault so the caret never moves / the form never submits.
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          el.call('moveNavSearchSelection', 1)
          return
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          el.call('moveNavSearchSelection', -1)
          return
        }
        if (e.key === 'Enter') {
          e.preventDefault()
          el.call('activateNavSearchSelection')
          return
        }
        if (e.key !== 'Escape') return
        // Nothing to close: the morph is already down (focus came back to the
        // hidden field after a dialog closed — live on dev 2ffb10c23,
        // 2026-10-01). Leave the key for the layer below. `false`, not falsy:
        // where the morph flag is never used (MainNavbar's icon row) the field
        // still closes its own search.
        if (el?.state?.root?._headerSearchOpen === false) return
        // The close SPENDS the key: one Escape, one layer — a lower document
        // layer (home customize mode under the open search) must not act on
        // the press that closed the search (fw-wl, dev e004fe4ec, 2026-09-30).
        if (typeof e.preventDefault === 'function') e.preventDefault()
        if (typeof el?.context?.functions?.closeNavSearch === 'function') {
          el.call('closeNavSearch')
        }
        // Collapse the AppHeader search morph back to the tab bar (no-op
        // where _headerSearchOpen isn't used, e.g. MainNavbar's icon row).
        try {
          el.state?.root?.update?.({ _headerSearchOpen: false })
        } catch (err) {
          console.warn('[HeaderSearch] failed to clear _headerSearchOpen:', err)
        }
        // Clear the uncontrolled input too — closeNavSearch only resets state,
        // and residual text used to dead-end the next focus-open.
        if (el.node) el.node.value = ''
        el.node?.blur?.()
      }
    },

    // THE ⌘K HINT (DESIGN_SPEC §5.4 and the search launcher row: "⌘K keycap
    // in the pill", ref r072) — the kit's AppKeycap, one cap per key, shown
    // because the shortcut is wired (onDocumentKeydown above). It floats over
    // the Input's end, a `Y` step before the end control — here the chevron
    // (right A, box A); the AppHeader morph restates `right` for its Close X —
    // so the Input keeps the pill's FULL width and the global focus ring
    // outlines the whole pill. (An in-flow version shrank the Input and the
    // ring cut through the K cap — live on dev 2c2b6d978.) The caps show only
    // while the field is empty, so typed text never meets them, and the short
    // placeholder ends far before them; gone on a phone, which has no
    // shortcut keys.
    Shortcut: {
      position: 'absolute',
      right: 'calc(A + A + Y)',
      top: '50%',
      transform: 'translateY(-50%)',
      flow: 'x',
      align: 'center',
      gap: 'W',
      ariaHidden: 'true',
      pointerEvents: 'none',
      subscribeTo: ['navSearchQuery'],
      visibility: (el, s) => ((s.root?.navSearchQuery || '').trim() ? 'hidden' : 'visible'),
      childExtends: 'AppKeycap',
      children: (el) => el.scope.shortcutKeys().map((text, i) => ({ key: 'k' + i, text })),
      '@mobileL': { display: 'none' }
    }
  },

  // Grouped-results renderer. SearchBar extends DropdownParentFocus which
  // CSS-shows the dropdown on `:focus-within` — so gate on `_navSearchOpen`
  // (set true by globalSearch on a non-empty query; cleared by closeNavSearch
  // / Escape) so the panel only appears once a real search is in flight.
  Dropdown: {
    // Element-level subscription so the hide flip is guaranteed to re-render
    // when globalSearch/closeNavSearch write the flag (the factory alone isn't
    // re-run reliably without it — same pattern as AppHeader's morph gate).
    // `_searchResultsInLauncher` (9A — the ONE search dropdown): a shell whose
    // launcher hosts the typed results itself declares it (the workspace
    // shell, state-app.js), and this panel then never shows — one dropdown,
    // never two. A shell that does not declare it keeps this panel as before.
    subscribeTo: ['_navSearchOpen'],
    hide: (el, s) => !s.root?._navSearchOpen || !!s.root?._searchResultsInLauncher,
    // `_navSearchOpen` (via hide) is the ONE visibility gate. The base
    // Dropdown ships opacity:0/visibility:hidden expecting the
    // DropdownParentFocus ':focus-within' reveal — dead CSS until
    // smbls@5b479eb85 (see the DONE ticket in tickets/smbls.md), and even
    // fixed it is the wrong gate for a QUERY-driven panel (focus ≠ has
    // results). Assert the visible state statically; presence is
    // state-driven above. The inherited ':focus-within' block still reaches
    // the pill — pseudo blocks DEEP-MERGE across extends (measured on the
    // served element 2026-09-24: z-index 1000 + this reveal, one rule) — and
    // its reveal is inert here because this panel is already visible and
    // state-gated. AppHeader adds no focus paint of its own: focus is the
    // global ring on the input.
    opacity: '1',
    visibility: 'visible',
    transform: 'translate3d(0,0,0)',

    // Neutralize the CANVAS-project schema search chrome this Dropdown
    // otherwise inherits from `SearchBar.Dropdown`'s `extends: [..,
    // 'CanvasSearchDropdown']` (a "Recent searches" / "Search results"
    // caption + a Fuse.js list over `s.recents`/`s.searchTerm` — state this
    // GLOBAL cross-entity search never populates). Left un-overridden, that
    // inherited block is exactly the SEARCH-1 "static 'Recent searches'
    // header over blank space, indistinguishable from mid-fetch" bug: it
    // renders unconditionally, in a context whose data it can never have.
    // ResultGroups/Loading/NoResults below are this search's real,
    // purpose-built states. `if:` (unmount), not `hide:` (display:none) —
    // Items.onInit dynamic-imports fuse.js and scans the canvas project
    // schema; that work must never run at all in a context with no project
    // schema to scan, not just render invisibly.
    CaptionTitle: { if: () => false },
    Items: { if: () => false },

    // Loading indicator (SEARCH-1): shown for the exact window
    // `_navSearchLoading` covers — from the keystroke that fired this query
    // until ITS OWN results land (superseded in-flight calls never flip it,
    // see globalSearch.js) — so this can never show "done" while a fetch for
    // the CURRENT query is still outstanding.
    Loading: {
      subscribeTo: ['_navSearchLoading'],
      hide: (el, s) => !s.root?._navSearchLoading,
      padding: 'Y1 A',
      fontSize: 'Z2',
      color: 'caption',
      text: '{{ shell.headerSearch.loading | polyglot }}'
    },

    // Explicit "no results" state (SEARCH-1) — previously a genuine
    // zero-result query showed nothing at all (just the inherited "Recent
    // searches" header above), indistinguishable from a fetch still in
    // flight. Only renders once loading has settled AND the query is
    // non-empty AND nothing matched.
    NoResults: {
      subscribeTo: ['_navSearchLoading', '_navSearchGrouped', 'navSearchQuery'],
      hide: (el, s) =>
        !!s.root?._navSearchLoading ||
        !(s.root?.navSearchQuery || '').trim() ||
        !!s.root?._navSearchGrouped?.length,
      padding: 'Y1 A',
      fontSize: 'Z2',
      color: 'caption',
      text: (el, s) =>
        el
          .call('polyglot', 'shell.headerSearch.noResults')
          .replace('{query}', (s.root?.navSearchQuery || '').trim())
    },

    ResultGroups: {
      flow: 'y',
      subscribeTo: ['_navSearchGrouped', '_navSearchLoading'],
      if: (el, s) => !!s.root?._navSearchGrouped?.length,
      // Stale-results fix: dim (never hide — hiding would flash the panel
      // empty on every keystroke) the previous query's rows the instant a
      // new query starts fetching, so they read as "not current" rather
      // than masquerading as the fresh answer for however long the debounce
      // + network round trip takes.
      opacity: (el, s) => (s.root?._navSearchLoading ? '0.45' : '1'),
      transition: 'opacity A defaultBezier',
      children: (el, s) => s.root?._navSearchGrouped || [],
      childExtends: 'NavSearchResultGroup',
      childrenAs: 'state'
    }
  }
}
